/**
 * FileMakerODataConfig - FileMaker OData source configuration
 * 
 * CHANGE HISTORY:
 * - 251201_0315 danielbeer: Created FileMaker OData configuration UI
 */

import React, { useState } from 'react';
import { ODataQueryBuilder } from './ODataQueryBuilder';
import type { FileMakerODataConfig as ODataConfigType } from '../../types/FileMakerTypes';
import './DataSourceConfig.css';

export interface FileMakerODataConfigProps {
  config: ODataConfigType;
  onChange: (config: Partial<ODataConfigType>) => void;
  onTest?: () => Promise<void>;
}

export function FileMakerODataConfig({
  config,
  onChange,
  onTest
}: FileMakerODataConfigProps) {
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
    recordCount?: number;
  } | null>(null);

  const handleTest = async () => {
    setIsTesting(true);
    setTestResult(null);
    try {
      await onTest?.();
      setTestResult({
        success: true,
        message: 'Connection successful',
        recordCount: 0 // TODO: Get actual count
      });
    } catch (error: any) {
      setTestResult({
        success: false,
        message: error.message || 'Connection failed'
      });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <div className="filemaker-odata-config">
      <div className="config-section">
        <h4>Connection Settings</h4>
        
        <div className="form-group">
          <label>Server</label>
          <input
            type="text"
            value={config.server || ''}
            onChange={(e) => onChange({ server: e.target.value })}
            placeholder="fm.example.com"
          />
          <span className="form-hint">FileMaker Server hostname or IP</span>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Database</label>
            <input
              type="text"
              value={config.database || ''}
              onChange={(e) => onChange({ database: e.target.value })}
              placeholder="Contacts"
            />
          </div>

          <div className="form-group">
            <label>Table/Layout</label>
            <input
              type="text"
              value={config.table || ''}
              onChange={(e) => onChange({ table: e.target.value })}
              placeholder="CONTACTS"
            />
          </div>
        </div>
      </div>

      <div className="config-section">
        <h4>Authentication</h4>
        
        <div className="form-group">
          <label>Username</label>
          <input
            type="text"
            value={config.authentication?.username || ''}
            onChange={(e) => onChange({ 
              authentication: { 
                ...config.authentication,
                type: 'basic',
                username: e.target.value 
              }
            })}
            placeholder="admin"
            autoComplete="username"
          />
        </div>

        <div className="form-group">
          <label>Password</label>
          <input
            type="password"
            value={config.authentication?.password || ''}
            onChange={(e) => onChange({ 
              authentication: { 
                ...config.authentication,
                type: 'basic',
                password: e.target.value 
              }
            })}
            placeholder="••••••••"
            autoComplete="current-password"
          />
          <span className="form-hint">
            ⚠️ Passwords will be stored in Profylr. External keystore support coming soon.
          </span>
        </div>
      </div>

      <div className="config-section">
        <h4>Query (OData $filter)</h4>
        
        <div className="form-group">
          <textarea
            value={config.query?.filter || ''}
            onChange={(e) => onChange({ 
              query: { 
                ...config.query,
                filter: e.target.value 
              }
            })}
            placeholder="Status eq 'Active' and Type eq 'Customer'"
            rows={4}
          />
          <span className="form-hint">
            OData filter syntax. Example: <code>Status eq 'Active'</code>
          </span>
        </div>

        <ODataQueryBuilder
          onQueryChange={(filter) => onChange({ 
            query: { 
              ...config.query,
              filter 
            }
          })}
          onGetFields={async () => {
            // TODO: Call FileMaker script to get field list
            // For now, return empty array
            // When running in WebViewer, this can call:
            // window.FileMaker?.PerformScript('GetTableFields', JSON.stringify({ table: config.table }))
            return [];
          }}
          initialFilter={config.query?.filter}
        />

        <div className="form-row">
          <div className="form-group">
            <label>Order By</label>
            <input
              type="text"
              value={config.query?.orderBy || ''}
              onChange={(e) => onChange({ 
                query: { 
                  ...config.query,
                  orderBy: e.target.value 
                }
              })}
              placeholder="LastName asc, FirstName asc"
            />
          </div>

          <div className="form-group">
            <label>Top (Limit)</label>
            <input
              type="number"
              value={config.query?.top || ''}
              onChange={(e) => onChange({ 
                query: { 
                  ...config.query,
                  top: parseInt(e.target.value) || undefined 
                }
              })}
              placeholder="1000"
              min="1"
            />
          </div>
        </div>
      </div>

      <div className="config-actions">
        <button 
          onClick={handleTest}
          disabled={isTesting || !config.server || !config.database || !config.table}
          className="btn btn-secondary"
        >
          {isTesting ? '⟳ Testing...' : '🔍 Test Connection'}
        </button>

        {testResult && (
          <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
            <span className="result-icon">
              {testResult.success ? '✓' : '✗'}
            </span>
            <span className="result-message">{testResult.message}</span>
            {testResult.recordCount !== undefined && (
              <span className="result-count">
                {testResult.recordCount} records
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
